/* Interfaces */
interface ISearchHit {
    heading: string;
    url: string;
    preamble: string;
    lastUpdatedDate: string;
    lastUpdatedDateISO: string;
    hitType: string;
}

interface ISearchResult {
    searchHits: ISearchHit[];
    totalHits: number;
}

interface ITextContent {
    noKeywords: string;
    noResults: string;
    resultFor: string;
    resultsFor: string;
    updated: string;
    showMore: string;
}

/* HTML elements */
const searchForm = document.querySelector('.search-page__form') as HTMLFormElement;
const searchInput = document.querySelector('.search-page__input') as HTMLInputElement;
const searchSubmitButton = document.querySelector('.search-page__submit-button') as HTMLButtonElement;
const searchResultSection = document.querySelector('.search-page__result') as HTMLElement;
const searchResultInfo = document.querySelector('.search-page__result-info') as HTMLParagraphElement;

/* Data */
const { resultsToShow, resultsToAdd, currentLanguage, includeText } = searchForm.dataset;

/* Text content (translated text) */
let includeTextArray = includeText?.split(', ') as string[];
let textContent: ITextContent = {
    noKeywords: includeTextArray[0],
    noResults: includeTextArray[1],
    resultFor: includeTextArray[2],
    resultsFor: includeTextArray[3],
    updated: includeTextArray[4],
    showMore: includeTextArray[5]
};

/* Global variables */
let query: string | null = null;
let apiSkipAmount = 0 as number;
let apiTakeAmount = parseInt(resultsToShow!, 10) as number;

/* Functions – Reset and clear DOM */
const resetSearchResult = (): void => {
    searchInput.value = '';
    searchResultSection.classList.add('sr-only');
    searchResultInfo.textContent = textContent.noKeywords;
}

const clearResultList = (): void => {
    const resultList: HTMLUListElement | null = document.querySelector('.search-page__result-list');
    const showMore: HTMLDivElement | null = document.querySelector('.search-page__show-more');

    if (resultList) {
        resultList.remove();
    }

    if (showMore) {
        showMore.remove();
    }
}

/* Functions – Get data from API */
const getSearchResult = async (): Promise<ISearchResult | void> => {
    try {
        const response = await fetch(`/api/search?query=${query}&take=${apiTakeAmount}&skip=${apiSkipAmount}&language=${currentLanguage}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(response.statusText);
        }

        return result;
    } catch (error) {
        console.log(error);
    }
}

/* Functions – Render DOM */
const renderResultItems = (searchHits: ISearchHit[], resultList: HTMLUListElement): void => {
    searchHits.forEach(item => {
        const resultItem = document.createElement('li') as HTMLLIElement;
        resultItem.classList.add('search-page__result-item');

        if (item.hitType === 'Media') {
            resultItem.classList.add('search-page__result-item--media');
        }

        const link = document.createElement('a') as HTMLAnchorElement;
        link.classList.add('search-page__result-item-link');
        link.setAttribute('href', item.url);
        resultItem.appendChild(link);

        if (item.hitType === 'Media') {
            link.classList.add('search-page__result-item-link--icon-document');
        }

        const date = document.createElement('p') as HTMLParagraphElement;
        date.classList.add('search-page__result-item-date');
        date.innerHTML = `<span class="sr-only">${textContent.updated}: </span>`;
        link.appendChild(date);

        // Removed for now
        // const time = document.createElement('time') as HTMLTimeElement;
        // time.setAttribute('datetime', item.lastUpdatedDateISO);
        // time.textContent = item.lastUpdatedDate;
        // date.appendChild(time);

        const heading = document.createElement('h3') as HTMLHeadingElement;
        heading.classList.add('search-page__result-item-heading');
        heading.textContent = item.heading.replace(/&shy;/g, '\u00AD');;
        link.appendChild(heading);

        if (item.preamble) {
            const preamble = document.createElement('p') as HTMLParagraphElement;
            preamble.classList.add('search-page__result-item-preamble');
            preamble.textContent = item.preamble;
            link.appendChild(preamble);
        }

        resultList.appendChild(resultItem);
    });
}

const renderShowMoreButton = (): void => {
    const showMoreContainer = document.createElement('div');
    showMoreContainer.classList.add('search-page__show-more');

    const showMoreButton = document.createElement('button');
    showMoreButton.classList.add('search-page__show-more-button', 'button', 'button--secondary');
    showMoreButton.setAttribute('type', 'button');
    showMoreButton.textContent = textContent.showMore;

    showMoreButton.addEventListener('click', (event: MouseEvent) => {
        addMoreResultItems(showMoreButton);
    });

    showMoreContainer.appendChild(showMoreButton);
    searchResultSection.appendChild(showMoreContainer);
}

const renderResult = (searchResult: ISearchResult): void => {
    clearResultList();

    if (searchResult.totalHits > 0) {
        if (searchResult.totalHits === 1) {
            searchResultInfo.innerHTML = `${searchResult.totalHits} ${textContent.resultFor} <strong>${query}</strong>.`;
        } else {
            searchResultInfo.innerHTML = `${searchResult.totalHits} ${textContent.resultsFor} <strong>${query}</strong>.`;
        }

        const resultList = document.createElement('ul') as HTMLUListElement;
        resultList.classList.add('search-page__result-list');
        searchResultSection.appendChild(resultList);

        renderResultItems(searchResult.searchHits, resultList);

        if (searchResult.searchHits.length < searchResult.totalHits) {
            renderShowMoreButton();
        }
    } else {
        searchResultInfo.textContent = textContent.noResults;
    }

    searchResultSection.classList.remove('sr-only');
}

const renderWithoutResult = (): void => {
    clearResultList();

    searchInput.value = '';
    searchResultInfo.textContent = textContent.noKeywords;
    searchResultSection.classList.remove('sr-only');
}

/* Functions - Search */
const search = async (): Promise<void> => {
    apiSkipAmount = 0;

    let searchResult = await getSearchResult();
    if (searchResult) {
        renderResult(searchResult);
    }
}

const searchFromQueryString = (): void => {
    const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
    let queryFromParam: string | null = urlParams.get('query');
    let takeFromParam: string | null = urlParams.get('take');

    query = queryFromParam;

    if (query) {
        query = query.trim();
        searchInput.value = query;
    } else {
        query = '';
    }

    if (takeFromParam && !isNaN(parseInt(takeFromParam, 10))) {
        apiTakeAmount = parseInt(takeFromParam, 10);
    }

    if (query.length > 0) {
        search();
    } else {
        renderWithoutResult();
    }
}

const addMoreResultItems = async (showMoreButton: HTMLButtonElement): Promise<void> => {
    apiSkipAmount += apiTakeAmount;
    apiTakeAmount = parseInt(resultsToAdd!, 10);

    let searchResult = await getSearchResult();
    if (searchResult) {
        const resultList = document.querySelector('.search-page__result-list') as HTMLUListElement;
        const resultItemsCount = resultList.children.length as number;

        let takeAmount = resultItemsCount + parseInt(resultsToAdd!, 10);

        if (searchResult.totalHits < takeAmount) {
            takeAmount = searchResult.totalHits;
        }

        window.history.replaceState({},'', `?query=${query}&take=${takeAmount}`);

        renderResultItems(searchResult.searchHits, resultList);

        if (resultList.children.length === searchResult.totalHits) {
            showMoreButton.remove();
        }

        let resultItems = resultList.children as HTMLCollection;
        let firstAddedResultItem = resultItems[resultItemsCount] as HTMLLIElement;
        let firstAddedResultItemLink = firstAddedResultItem.querySelector('a') as HTMLAnchorElement;
        firstAddedResultItemLink.focus();
    }
}

/* Event listeners */

// Event that happens if someone enters url with query string
window.addEventListener('load', (): void => {
    if (window.location.search) {
        searchFromQueryString();
    }

    searchInput.focus();
});

// Event that happens when users browse through their browser history, e.g. use the back button.
window.addEventListener('popstate', (): void => {
    if (window.location.search) {
        searchFromQueryString();
    } else {
        resetSearchResult();
    }

    searchInput.focus();
});

// Event that happens when user submits a search with the form
searchForm.addEventListener('submit', (event: SubmitEvent): void => {
    event.preventDefault();

    query = searchInput.value.trim();

    window.history.pushState({},'', `?query=${query}`);

    if (query.length > 0) {
        search();
    } else {
        renderWithoutResult();
    }
});